import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "react-bootstrap-icons";
import { Spinner } from "react-bootstrap";
import qs from "qs";
import "./Upload.css";

const ContractDropDown = ({
  selectedId,
  onChange,
  api,
  defaultContract,
  customerCode,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(defaultContract ?? null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const search = useCallback(
    (searchTerm) => {
      const searchObj = {
        Query: searchTerm,
        Options: {
          Page: 1,
          ResultsPerPage: 10,
          Sort: { Name: "contractnumber", Descending: false },
        },
      };

      const queryString = qs.stringify(searchObj, {
        allowDots: true,
        encode: false,
        addQueryPrefix: true,
      });

      setIsLoading(true);
      api
        .get("/api/contracts/search" + queryString)
        .then((response) => (response.status === 200 ? response.data : null))
        .then((data) => {
          setResults(
            data?.results?.filter(
              (c) => c.provider?.customerCode === customerCode,
            ) || [],
          );
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    },
    [api],
  );

  // Debounce query changes
  useEffect(() => {
    if (!open || !query) return;

    const handler = setTimeout(() => {
      search(query);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [query, open, search]);

  // Load results when first opened
  useEffect(() => {
    if (open) search("");
  }, [open]);

  const handleToggle = () => {
    if (!open) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpen((o) => !o);
    setQuery("");
  };

  const handleSelect = (contract) => {
    setSelected(contract);
    onChange(contract);
    setOpen(false);
    setQuery("");
  };

  const triggerClass = [
    "contract-dropdown__trigger",
    open ? "is-open" : "",
    selected ? "has-value" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={wrapperRef} className="contract-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={handleToggle}
      >
        {selected ? (
          <span className="contract-dropdown__selected">
            <span className="contract-dropdown__code">
              {selected.number}
              {selected.providerNumber &&
              selected.providerNumber !== selected.number
                ? ` - ${selected.providerNumber}`
                : ""}
            </span>
            {selected.name}
          </span>
        ) : (
          <span>Search contract…</span>
        )}
        <ChevronDown size={11} color="#BBB" />
      </button>

      {open && (
        <div
          className="contract-dropdown__menu"
          style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
        >
          <div className="contract-dropdown__search">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Results shown as: ClearPoint Contract # - Provider Contract #"
            />
          </div>
          <div className="contract-dropdown__list">
            {isLoading ? (
              <div className="contract-dropdown__empty">
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: "6px" }}
                />
                Searching…
              </div>
            ) : results.length === 0 ? (
              <div className="contract-dropdown__empty">No contracts found</div>
            ) : (
              results.map((c) => (
                <div
                  key={c.id}
                  className={`contract-dropdown__option${selectedId === c.id ? " is-selected" : ""}`}
                  onClick={() => handleSelect(c)}
                >
                  <div className="contract-dropdown__option-name">
                    {c.number}
                    {c?.providerNumber && c?.providerNumber !== c.number
                      ? ` - ${c.providerNumber}`
                      : ""}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDropDown;
